import { useState } from 'react'
import { useParams } from 'react-router'
import { FormError } from '@/components/FormError'
import {
  BookBuilder,
  Choice,
  Kept,
  LookTile,
  NoPageEdit,
  PageEdit,
  Section,
  YourLook,
  type Trim,
} from '@/components/print/Builder'
import { BookFaceView, type BookFace } from '@/components/print/BookPages'
import { MakeFiles } from '@/components/print/MakeFiles'
import { useAlbum, type Album } from '@/lib/albums'
import { usePageFrame } from '@/lib/frame'
import { printableStorybook } from '@/lib/print/build'
import { editPage, useDesign } from '@/lib/print/design'
import { FINISHES, SCREEN } from '@/lib/print/finishes'
import {
  CLASSIC,
  LOOK_IDS,
  LOOKS,
  photoBookPlan,
  sameLook,
  readChosen,
  type Chosen,
  type Edges,
  type Look,
  type LookId,
  type PageEdit as PageChoices,
} from '@/lib/print/looks'
import { formatById } from '../../shared/print'
import { ScreenLoader } from '@/components/Loader'

export function PrintBookPage() {
  const { id = '' } = useParams()
  const album = useAlbum(id)
  if (album.isPending) return <ScreenLoader surface="table" />
  if (album.isError) return <FormError error={album.error} />
  return <Builder album={album.data} />
}

// The photo book builder: pick a look, change anything about it, and turn the pages of the book that will be printed.
// The preview and the print files come from the same plan, so what's turned here is what's printed.
function Builder({ album }: { album: Album }) {
  usePageFrame(album.familyId, {
    title: 'Print this book',
    back: { to: `/album/${album.id}/story`, label: 'Back to the storybook' },
  })
  const {
    design: chosen,
    choose,
    keeping,
  } = useDesign<Chosen>({
    book: { kind: 'album', id: album.id },
    storageKey: `gf.print-look.${album.id}`,
    kept: album.printDesign ?? null,
    mine: album.mine,
    read: readChosen,
    start: { from: 'classic', look: CLASSIC },
  })
  const [finish, setFinish] = useState('pdf')
  const [busy, setBusy] = useState(false)
  const look = chosen.look
  const format = formatById(finish)
  const layout = format ? { trim: format.trim, minPages: format.minPages, bound: true } : SCREEN
  const trim = layout.trim
  const plan = photoBookPlan(album, look, layout, chosen.pages)
  const custom = !sameLook(look, LOOKS[chosen.from].look)

  const change = <K extends keyof Look>(key: K, value: Look[K]) =>
    choose({ ...chosen, look: { ...look, [key]: value } })
  // A page's own choices, kept by the photo it starts with.
  const editOne = (id: string, edit: PageChoices) => choose({ ...chosen, pages: editPage(chosen.pages, id, edit) })

  // The whole book: its cover, the endpaper inside it, the printed pages, and the back.
  const faces: BookFace[] = [
    { kind: 'cover' },
    { kind: 'endpaper' },
    ...plan.pages,
    { kind: 'endpaper' },
    { kind: 'back' },
  ]
  const numbered = (i: number) => (i >= 2 && i < faces.length - 2 ? i - 1 : undefined)

  return (
    <BookBuilder
      label={`Print ${album.title}, Storybook version`}
      badge="Storybook version"
      back={{ to: `/album/${album.id}/story`, label: 'the storybook' }}
      trim={trim}
      photos={look.photos}
      faces={faces.map((face, i) => () => (
        <BookFaceView face={face} album={album} look={look} trim={trim} number={numbered(i)} />
      ))}
      pages={plan.pages.length}
      thisPage={(shown) => {
        const editable = shown.flatMap((i) => {
          const face = faces[i]!
          return face.kind === 'photos' ? [{ face, number: numbered(i)! }] : []
        })
        if (!editable.length) return <NoPageEdit>Turn to a page of photos to change just that page.</NoPageEdit>
        return editable.map(({ face, number }) => {
          const id = face.photos[0]!.id
          const edit = chosen.pages?.[id] ?? {}
          return (
            <PageEdit
              key={id}
              name={`Page ${number}`}
              edited={Boolean(chosen.pages?.[id])}
              onReset={() => editOne(id, {})}
            >
              <Choice
                label="Photos on this page"
                value={edit.most ?? (face.photos.length as Look['most'])}
                options={[
                  [1, '1'],
                  [2, '2'],
                  [3, '3'],
                  [4, '4'],
                ]}
                onChange={(v) => editOne(id, { ...edit, most: v })}
              />
              <Choice<Edges | ''>
                label="Edges"
                value={edit.edges ?? ''}
                options={[
                  ['', 'As the book'],
                  ['bleed', 'Edge to edge'],
                  ['thin', 'Thin'],
                  ['border', 'Border'],
                  ['wide', 'Wide'],
                ]}
                onChange={(v) => editOne(id, { ...edit, edges: v || undefined })}
              />
            </PageEdit>
          )
        })
      }}
    >
      <Section title="Start with a look">
        <div className="grid grid-cols-2 gap-2.5" role="radiogroup" aria-label="Looks">
          {LOOK_IDS.map((id) => (
            <AlbumLookTile
              key={id}
              id={id}
              album={album}
              trim={trim}
              layout={layout}
              checked={chosen.from === id && !custom}
              onPick={() => choose({ ...chosen, from: id, look: LOOKS[id].look })}
            />
          ))}
        </div>
        {custom && (
          <YourLook
            from={LOOKS[chosen.from].name}
            onReset={() => choose({ ...chosen, look: LOOKS[chosen.from].look })}
          />
        )}
        <Kept mine={album.mine} keeping={keeping} noun="album" by={album.by ?? ''} />
      </Section>

      <Section title="Customize">
        <Choice
          label="Photos a page"
          hint="At most; each year’s photos are shared evenly."
          value={look.most}
          options={[
            [1, '1'],
            [2, '2'],
            [3, '3'],
            [4, '4'],
          ]}
          onChange={(v) => change('most', v)}
        />
        <Choice
          label="Edges"
          value={look.edges}
          options={[
            ['bleed', 'Edge to edge'],
            ['thin', 'Thin'],
            ['border', 'Border'],
            ['wide', 'Wide'],
          ]}
          onChange={(v) => change('edges', v)}
        />
        <Choice
          label="Type"
          value={look.type}
          options={[
            ['modern', 'Modern'],
            ['traditional', 'Traditional'],
          ]}
          onChange={(v) => change('type', v)}
        />
        <Choice
          label="Captions"
          value={look.captions}
          options={[
            [true, 'Show'],
            [false, 'Hide'],
          ]}
          onChange={(v) => change('captions', v)}
        />
        <Choice
          label="Paper"
          value={look.paper}
          options={[
            ['white', 'White'],
            ['natural', 'Natural'],
          ]}
          onChange={(v) => change('paper', v)}
        />
        <Choice
          label="Photo color"
          hint="How the photos print. The photos in the app stay as they are."
          value={look.photos}
          options={[
            ['color', 'Color'],
            ['mono', 'Black and white'],
            ['sepia', 'Sepia'],
          ]}
          onChange={(v) => change('photos', v)}
        />
        <Choice
          label="Cover"
          value={look.cover}
          options={[
            ['photo', 'Photo'],
            ['window', 'Window'],
            ['words', 'Words'],
          ]}
          onChange={(v) => change('cover', v)}
        />
        {look.edges === 'bleed' || look.edges === 'thin' ? (
          <p className="text-sub text-muted-foreground">
            Photos are cropped to fill their space on the page. The photos in the album stay as they are.
          </p>
        ) : null}
      </Section>

      <Section title="Size and finish">
        <Choice
          label="Make it as"
          value={finish}
          disabled={busy}
          options={FINISHES.map((x) => [x.id, x.label] as [string, string])}
          onChange={setFinish}
        />
        <p className="text-sub text-muted-foreground">
          {FINISHES.find((x) => x.id === finish)!.hint}. {plan.pages.length} pages
          {format && plan.added > 0
            ? `, with ${plan.added} ruled ${plan.added === 1 ? 'page' : 'pages'} for notes to reach the ${format.binding}’s ${format.minPages}.`
            : '.'}
        </p>
        <MakeFiles
          key={`${finish}:${JSON.stringify(chosen)}`}
          book={printableStorybook(album, look, chosen.pages)}
          formatId={finish}
          filler="More photos"
          onBusy={setBusy}
        />
      </Section>
    </BookBuilder>
  )
}

// A named look, shown as one of this album's own pages in it.
function AlbumLookTile({
  id,
  album,
  trim,
  layout,
  checked,
  onPick,
}: {
  id: LookId
  album: Album
  trim: Trim
  layout: { minPages: number; bound: boolean }
  checked: boolean
  onPick: () => void
}) {
  const { name, blurb, look } = LOOKS[id]
  const sample = photoBookPlan(album, look, layout).pages.find((x) => x.kind === 'photos') ?? { kind: 'title' as const }
  return (
    <LookTile name={name} blurb={blurb} checked={checked} onPick={onPick}>
      <span className="relative block h-full shadow-paper" style={{ aspectRatio: `${trim.width} / ${trim.height}` }}>
        <BookFaceView face={sample} album={album} look={look} trim={trim} />
      </span>
    </LookTile>
  )
}
